import { Entity, Coordinate, EntityId, TileSize } from "game-common/models"
import { Application, Graphics, RenderTexture } from "pixi.js"

import { ClientGameLogic } from "../../../client_game_logic"
import { logger } from "../../../logger"
import { ClientRenderer } from "../../../client_models"
import { EntityRenderingManager } from "./entity_rendering_manager"
import { GuiManager } from "./gui_manager"
import { FactionTracker } from "./gui/faction_tracker"
import { Keyboard } from "./input/keyboard"
import { Mouse } from "./input/mouse"
import { MapRenderingManager } from "./map_rendering_manager"
import { MinimapSurface } from "./minimap_surface"
import { ShadowRendering } from "./shadow_rendering"
import { ShadowPerformanceMonitor } from "./shadow_performance_monitor"
import { SpeechHoldButtons } from "./gui/speech_hold"
import { Butterflies } from "./effects/butterflies"
import { Fireflies } from "./effects/fireflies"
import { GrassSway } from "./effects/grass_sway"
import { NightFog } from "./effects/night_fog"
import { WindLeaves } from "./effects/wind_leaves"
import { WorldOverlaySurface } from "./world_overlay_surface"
import { ScalingParams } from "../../../client_util"
import { PerformanceMonitor } from "./performance_monitor"

export class PixiJsClientRenderer implements ClientRenderer {
    static _app: Application
    private app: Application
    private _gui: GuiManager
    private _factionTracker: FactionTracker
    private _windLeaves: WindLeaves
    private _nightFog: NightFog
    private _grassSway: GrassSway
    private _butterflies: Butterflies
    private _fireflies: Fireflies
    private _entity: EntityRenderingManager
    private _map: MapRenderingManager
    private _surface: Graphics
    private _worldOverlay: WorldOverlaySurface

    private _mouse: Mouse
    private _keyboard: Keyboard

    private _logic: ClientGameLogic
    private debugSurface: Graphics

    private debugVision = false
    private shadowRendering: ShadowRendering
    private shadowPerformanceMonitor: ShadowPerformanceMonitor
    private performanceMonitor: PerformanceMonitor
    private cachedSortedEntities: Array<{ entity: any; distSquared: number }> | null = null
    private frameTimeHistory: number[] = []
    private lastFrameTime = 0
    private targetFrameTime = 1000 / 60 // 60 FPS target
    private maxEntitiesPerFrame = 200 // Simple limit for basic culling
    private performanceHistory: number[] = [] // Track performance over time

    constructor(app: Application, logic: ClientGameLogic) {
        PixiJsClientRenderer._app = app
        this.app = app
        this._logic = logic
        const stage = this.app.stage
        stage.interactive = true
        stage.sortableChildren = true

        const surface = new Graphics()
        surface.sortableChildren = true
        stage.addChild(surface)
        surface.scale.set(ScalingParams.surfaceZoom)
        this._surface = surface

        const worldOverlaySurface = new WorldOverlaySurface(logic)
        stage.addChild(worldOverlaySurface)
        this._worldOverlay = worldOverlaySurface

        // WHICH LAYERS ARE THE WORLD. Button presses arrive on a document-level mousedown, which pixi
        // never hit-tests, so before this every click reached the player controller wherever it landed
        // and pressing a HUD control swung the weapon at whatever was behind it. Naming the world's
        // layers lets Mouse ask what is under the pointer and drop presses that belong to the HUD.
        //
        // Everything else on the stage is chrome: the gui container, the faction tracker, debug.
        Mouse.setWorldRoots([surface, worldOverlaySurface])

        // This one is still needed and is NOT the same problem. A held bubble's "Next" is drawn on the
        // entity's own sprite, INSIDE the world surface, so it is a world press by every test above --
        // it has to say so itself. Read the controller lazily: it does not exist yet here.
        SpeechHoldButtons.bindSuppressAttack(() => logic.playerController?.suppressAttack())

        const minimapSurface = new MinimapSurface(logic)
        this._gui = new GuiManager(stage, minimapSurface, logic)
        this._gui.init()

        // Screen-space overlay for faction-tracking edge arrows (reads state from logic).
        this._factionTracker = new FactionTracker(logic, surface, app)
        stage.addChild(this._factionTracker.container)

        ////////////////////////////////////////

        const skySurface = new Graphics()
        skySurface.sortableChildren = true
        skySurface.zIndex = 1000000

        // The wind bending the grass (see GrassSway). Built before the map, because the map hands it
        // every sprited cell as it renders one.
        this._grassSway = new GrassSway()

        this._map = new MapRenderingManager(surface, skySurface, minimapSurface, worldOverlaySurface, logic)
        this._map.setGrassSway(this._grassSway)
        this._map.init()

        this._entity = new EntityRenderingManager(surface, minimapSurface, worldOverlaySurface, logic, this, app)
        this._entity.init()

        // Windblown leaves (see WindLeaves): a per-room ambience, off unless the room asks for it.
        // It goes INSIDE the world surface so the leaves parallax with the camera and inherit the
        // night-time visibility mask; its zIndex keeps them above entities but under the sky/roof.
        this._windLeaves = new WindLeaves(WindLeaves.textureFactory(app.renderer as any))
        surface.addChild(this._windLeaves.container)

        // Night fog (see NightFog): one shader on one quad, also inside the world surface so it
        // parallaxes and is clipped by the night visibility mask. It asks the surface itself what world
        // rectangle is on screen, which is the only way to be right at the map edges where the camera
        // stops following the player. Drawn under the leaves, so leaves stay crisp against it.
        this._nightFog = new NightFog(surface, app.screen, NightFog.bakeNoise)
        surface.addChild(this._nightFog.container)

        // Butterflies by day (see Butterflies), the day-side counterpart to the fog. In the world
        // surface with the rest, so they parallax and are clipped by the visibility mask.
        // The perch source is the grass registry: butterflies settle on the same tufts the wind bends.
        this._butterflies = new Butterflies(Butterflies.textureFactory(app.renderer as any), (x, y, r) =>
            this._grassSway.perchNear(x, y, r),
        )
        surface.addChild(this._butterflies.container)

        // Fireflies, after dark. NOT in the world surface with the others: that surface is masked by
        // the night visibility, and a firefly is a light -- the whole picture is one glowing out in the
        // dark where you cannot otherwise see. The overlay surface is camera-moved but unmasked.
        this._fireflies = new Fireflies(Fireflies.textureFactory(app.renderer as any))
        worldOverlaySurface.addChild(this._fireflies.container)

        this._mouse = new Mouse()
        this._keyboard = new Keyboard()

        const debugSurface = new Graphics()
        this.debugSurface = debugSurface
        debugSurface.sortableChildren = true

        surface.addChild(skySurface)
        stage.addChild(debugSurface)

        this.shadowRendering = new ShadowRendering(app, surface, this.debugVision, logic)
        this.shadowPerformanceMonitor = new ShadowPerformanceMonitor(this.shadowRendering)

        // Initialize performance monitor
        this.performanceMonitor = new PerformanceMonitor({
            enableMetrics: true,
            logInterval: 5000,
            enableMemoryTracking: true,
            enableDetailedStats: true,
        })

        // Set up performance monitor callbacks
        this.performanceMonitor.setStatCallbacks({
            getViewportCullingStats: () => {},
        })
    }

    gui = () => this._gui
    entity = () => this._entity
    map = () => this._map
    mouse = () => this._mouse
    keyboard = () => this._keyboard
    logic = () => this._logic

    getApp = () => this.app

    // Follow-cam driver: while the ember-roster PiP modal is open, refresh the focused entity's
    // neighborhood (the main loop culls to ~7 tiles around the player, so far focus entities would
    // otherwise be frozen) and render the world centred on it into the modal's texture.
    private updateFollowCam = () => {
        const focusId: EntityId | null = this._logic.focusEntityId
        const followCam = this._gui.followCam
        // Skip the render-to-texture while the PiP is open-but-hidden (subject is on-screen).
        if (!focusId || !followCam || !followCam.isOpen() || !followCam.visible) {
            return
        }
        const focus = this._logic.getEntity(focusId)
        if (!focus) {
            return
        }
        this.renderFocusNeighborhood(focus.entityId)
        this.renderWorldToTexture(followCam.getTexture(), focus.location.x, focus.location.y, followCam.getZoom())
    }

    // Update renderables for entities near the focused entity so the PiP shows live movement even
    // when that neighborhood is off the player's screen. Bounded work: only runs while the modal
    // is open, and only touches entities the client already knows about.
    private renderFocusNeighborhood = (focusEntityId: EntityId) => {
        const playerEntityId = this._logic.playerEntity.entityId
        this._logic.entityManager.iterate(null, (entity: Entity) => {
            if (entity.entityId === playerEntityId) {
                return
            }
            if (this._logic.isDestroyed(entity.entityId)) {
                return
            }
            if (!this._logic.isNearFocus(entity.location)) {
                return
            }
            this._entity.render(entity, playerEntityId)
        })
    }

    // Render the world (`surface`) into `renderTexture`, centred on world coords (cx, cy), for the
    // follow-cam PiP. Temporarily re-aims the surface transform, then restores it before the normal
    // on-screen render in the same frame. Render square (equal x/y zoom): the PiP sprite lives in
    // the HUD, which is a child of the stage and so already inherits the stage's vertical SCALER —
    // baking SCALER in here too would double it (the vertical stretch bug).
    renderWorldToTexture = (renderTexture: RenderTexture, cx: number, cy: number, zoom: number) => {
        const surface = this._surface
        if (!surface) {
            return
        }

        const px = renderTexture.width / 2 - cx * zoom
        const py = renderTexture.height / 2 - cy * zoom

        const savedX = surface.position.x
        const savedY = surface.position.y
        const savedScaleX = surface.scale.x
        const savedScaleY = surface.scale.y

        surface.scale.set(zoom, zoom)
        surface.position.set(px, py)

        // Swap in a focus-centred darkness/light mask for this render only. The main mask
        // (this.surface.mask, set by ShadowRendering) is baked in the PLAYER's screen space, so
        // reusing it here makes lights drift across the PiP as the player walks. renderPipLightMask
        // rebakes the same lights in the PiP's framing so a light stays fixed over the world it lit.
        const savedMask = surface.mask
        const pipMask = this.shadowRendering.renderPipLightMask({
            cx,
            cy,
            zoom,
            texW: renderTexture.width,
            texH: renderTexture.height,
            resolution: renderTexture.baseTexture.resolution,
            visibility: this._map.getVisibility(),
            lights: this._entity.getLights(),
        })
        surface.mask = pipMask ?? null

        PixiJsClientRenderer._app.renderer.render(surface, { renderTexture, clear: true })

        surface.mask = savedMask
        surface.position.set(savedX, savedY)
        surface.scale.set(savedScaleX, savedScaleY)

        // Composite the world-overlay layer (name tags, hp, and speech bubbles) on top, so the PiP
        // shows the same in-world bubbles as the main view. Overlay items are positioned at
        // world*surfaceZoom (vs the surface's raw-world children), so it takes the SAME position but
        // a scale of zoom/surfaceZoom to line up with the world underneath.
        const overlay = this._worldOverlay
        if (overlay) {
            const oSavedX = overlay.position.x
            const oSavedY = overlay.position.y
            const oSavedScaleX = overlay.scale.x
            const oSavedScaleY = overlay.scale.y

            overlay.scale.set(zoom / ScalingParams.surfaceZoom, zoom / ScalingParams.surfaceZoom)
            overlay.position.set(px, py)

            // A held bubble's "Next" button is a CONTROL, and inside the PiP it would be a picture
            // of one -- unclickable, and sitting over the words the player is trying to read. Hide
            // every live one for this pass only; the main view gets them back immediately after.
            SpeechHoldButtons.setHiddenForCapture(true)
            PixiJsClientRenderer._app.renderer.render(overlay, { renderTexture, clear: false })
            SpeechHoldButtons.setHiddenForCapture(false)

            overlay.position.set(oSavedX, oSavedY)
            overlay.scale.set(oSavedScaleX, oSavedScaleY)
        }
    }

    updatePlayerLocation = (entity: Entity, force?: boolean) => {
        this._map.updatePlayerLocation(entity, force)
    }

    // Get shadow performance metrics for debugging
    getShadowPerformanceMetrics = () => {
        return this.shadowPerformanceMonitor.getMetrics()
    }

    // Force shadow texture update (useful for debugging)
    forceShadowTextureUpdate = () => {
        this.shadowRendering.forceTextureUpdate()
    }

    // Debug methods for viewport culling
    enableTileBasedCulling = () => {
        // this._entity.enableTileBasedCulling()
    }

    getViewportDebugInfo = (playerLocation: Coordinate) => {
        // return this._entity.getViewportDebugInfo(playerLocation)
        return {}
    }

    render = () => {
        const currentTime = performance.now()
        const frameTime = currentTime - this.lastFrameTime
        this.lastFrameTime = currentTime

        // Track frame time history for smoothness analysis
        this.frameTimeHistory.push(frameTime)
        if (this.frameTimeHistory.length > 60) {
            this.frameTimeHistory.shift()
        }

        // Start performance monitoring
        this.performanceMonitor.startFrame()

        // world
        this._map.renderTileMap()

        // Start render timing
        this.performanceMonitor.startRender()

        // Optimized entity rendering with early exits and batching
        this._entity.beginFrame()
        this.renderEntitiesOptimized()

        // Fade entity tiles (trees/bushes) the local player is standing behind, so the player stays
        // visible. Runs after entities are laid out this frame (player location is current).
        if (this._logic.playerEntity) {
            this._map.applyOccluderFade(this._logic.playerEntity)
        }

        // End render timing
        this.performanceMonitor.endRender()

        // Follow-cam picture-in-picture (ember roster): keep the focused entity's neighborhood
        // updated and render the world centred on it into the PiP texture.
        this.updateFollowCam()

        // Anything that was on screen last frame but nothing rendered this frame (it left the
        // culled set, or the follow-cam stopped feeding its neighborhood) has to be hidden, or it
        // stays drawn where it last was while the real entity moves on -- the "ghost" NPC.
        this._entity.endFrame()

        this.shadowRendering.renderVisibility({
            location: this._logic.playerEntity.location,
            polyMap: this._map.getVisibilityMap(),
            visibility: this._map.getVisibility(),
            debug: this.debugVision ? this.debugSurface : undefined,
            lights: this._entity.getLights(),
        })

        // Update shadow performance monitoring
        this.shadowPerformanceMonitor.update()

        // Update GUI timer
        this._gui.updateTimer(frameTime)

        // Redraw the tether from a per-entity command wheel to its target (and close the wheel once
        // that target leaves the viewport). The wheel itself stays where it opened.
        this._gui.updateCommandWheelTether()

        // Same for the recruit role picker, which floats over its recruit.
        this._gui.updateRolePickerPosition()

        // Touch has no hover, so keep the outline on the interactable nearest the player. Throttled
        // internally and a no-op with a mouse.
        this._entity.updateInteractionFocus()

        // Update faction-tracking edge arrows (offscreen faction members)
        this._factionTracker.update()

        // Blow the room's leaves about (no-op, one boolean, in a room with no wind or after dark).
        // After the world is laid out, so the box is centred on where the player actually is this
        // frame. The brightness is what makes the wind die at dusk -- and it is passed as undefined
        // for a room with no day cycle (dayPhase null), which the effect reads as permanent day
        // rather than as pitch dark.
        const brightness = this._logic.dayPhase ? this._logic.currentBrightness : undefined
        const ambience = this._logic.ambience
        this._windLeaves.update(ambience?.windLeaves ?? null, this._logic.playerEntity?.location, brightness)

        // ...and roll the fog in, which is the same gate read backwards: it thickens as the wind drops.
        this._nightFog.update(ambience?.nightFog ?? null, brightness)

        // ...and bend the grass on the SAME gust that carries the leaves, so the two are one wind.
        this._grassSway.update(ambience?.windLeaves ?? null, this._logic.playerEntity?.location, brightness)

        // ...and let the butterflies out, which read that same gust to know when to shelter.
        this._butterflies.update(ambience?.butterflies ?? null, this._logic.playerEntity?.location, brightness)

        // ...and light the fireflies, which are the butterflies' opposite number after dark.
        this._fireflies.update(ambience?.fireflies ?? null, this._logic.playerEntity?.location, brightness)

        // End performance monitoring
        this.performanceMonitor.endFrame()

        // Adaptive frame rate limiting for smoother gameplay
        this.adaptiveFrameRateLimit()
    }

    private renderEntitiesOptimized = () => {
        const playerEntityId = this._logic.playerEntity.entityId
        let renderedCount = 0
        const playerPos = this._logic.playerEntity.location

        // First pass: render player entity (always highest priority)
        if (this._logic.playerEntity) {
            this._entity.render(this._logic.playerEntity, playerEntityId)
            renderedCount++
        }

        // Check if we need to re-sort entities
        const shouldResort = this._logic.shouldResortEntities(playerPos)

        let entitiesToProcess: Array<{ entity: any; distSquared: number }>

        if (shouldResort) {
            entitiesToProcess = this.calculateSortedEntitiesByDistance(playerPos)
            this.cachedSortedEntities = entitiesToProcess
        } else {
            entitiesToProcess = this.cachedSortedEntities!
        }

        if (!entitiesToProcess?.length) {
            return
        }

        // Get adaptive entity limit based on performance (cached)
        const maxEntitiesToRender = this.getCachedEntityLimit()

        // Render entities in distance order (closest first)
        // Note: entities are already culled to 7 tiles during sorting
        for (let i = 0; i < entitiesToProcess.length; i++) {
            const { entity } = entitiesToProcess[i]
            // Early exit if we've rendered too many entities this frame
            if (renderedCount >= maxEntitiesToRender) {
                // Budget, not culling: the rest keep showing whatever they last showed rather
                // than popping out of the world, so exempt them from the end-of-frame sweep.
                for (let j = i; j < entitiesToProcess.length; j++) {
                    this._entity.markDeferred(entitiesToProcess[j].entity.entityId)
                }
                break
            }

            // Skip if entity is the player (already rendered)
            if (entity.entityId === playerEntityId) {
                continue
            }

            // Skip if entity is destroyed
            if (this._logic.isDestroyed(entity.entityId)) {
                continue
            }

            // Render the entity
            if (this._entity.render(entity, playerEntityId)) {
                renderedCount++
            }
        }

        // Update performance monitor with entity counts
        this.updateEntityRenderingMetrics(renderedCount)
    }

    private calculateSortedEntitiesByDistance(playerPos: Coordinate): Array<{ entity: any; distSquared: number }> {
        // Use spatial indexing for O(log n) performance instead of O(n)
        return this._logic.getVisibleEntities(playerPos)
    }

    private getCullingDistanceSquared(): number {
        // Use unified culling system from ClientGameLogic
        return this._logic.getCullingDistanceSquared()
    }

    private cachedEntityLimit: number = 200
    private lastEntityLimitUpdate: number = 0
    private entityLimitUpdateInterval: number = 1000 // Update every 1 second

    private getCachedEntityLimit(): number {
        const now = performance.now()

        // Only update entity limit periodically to avoid expensive metrics calls
        if (now - this.lastEntityLimitUpdate > this.entityLimitUpdateInterval) {
            this.updateCachedEntityLimit()
            this.lastEntityLimitUpdate = now
        }

        return this.cachedEntityLimit
    }

    private updateCachedEntityLimit(): void {
        const metrics = this.performanceMonitor.getMetrics()
        const baseLimit = 200

        // Adjust based on performance
        if (metrics.frameRate < 30) {
            this.cachedEntityLimit = Math.floor(baseLimit * 0.6) // Reduce when performance is poor
        } else if (metrics.frameRate > 60) {
            this.cachedEntityLimit = Math.floor(baseLimit * 1.5) // Increase when performance is good
        } else {
            this.cachedEntityLimit = baseLimit
        }
    }

    private updateEntityRenderingMetrics(renderedCount: number): void {
        const totalEntities = this._logic.entityManager.size()
        const culledCount = totalEntities - renderedCount
        this.performanceMonitor.updateEntityCounts(totalEntities, renderedCount, culledCount)

        // Debug: Log entity counts every few seconds (reduced frequency)
        if (Math.random() < 0.01) {
            // 1% chance to log
            logger.debug(
                { tags: ["rendering", "entities"] },
                `Entity rendering: ${renderedCount}/${totalEntities} rendered, ${culledCount} culled`,
            )
        }
    }

    private adaptiveFrameRateLimit = () => {
        if (this.frameTimeHistory.length < 10) return

        // Calculate average frame time
        const avgFrameTime = this.frameTimeHistory.reduce((a, b) => a + b, 0) / this.frameTimeHistory.length
        const currentFPS = 1000 / avgFrameTime

        // Track performance history
        this.performanceHistory.push(currentFPS)
        if (this.performanceHistory.length > 30) {
            this.performanceHistory.shift()
        }

        // Get total entity count for scaling decisions
        const totalEntities = this._logic.entityManager.size()

        // If we're running too fast, add a small delay for smoother gameplay
        if (currentFPS > 120) {
            const delay = Math.min(2, 1000 / 120 - avgFrameTime)
            if (delay > 0) {
                // Use setTimeout to add a small delay without blocking
                setTimeout(() => {}, delay)
            }
        }

        // Temporarily disable adaptive scaling to debug performance
        // Keep entity limit at 500 for now
        this.maxEntitiesPerFrame = 500
    }
}
